import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DemoPanel, type DemoPhase } from "./DemoPanel";
import { formatDemoMoney, usdPerMillionTokens } from "./demoRate";
import { toTokens } from "./toTokens";

interface Exchange {
  prompt: string;
  answer: string;
}

interface DemoState {
  exchange: number;
  phase: DemoPhase;
  promptChars: number;
  tokens: number;
  spentTokens: number;
  months: number;
}

const allowanceUsd = 0.0015;

const jitter = (low: number, high: number): number => low + Math.random() * (high - low);

/**
 * A pretend model answering pretend prompts, token by token, while a meter counts the tokens and what they would cost and fills a
 * monthly allowance until it runs out: in miniature, the sum the portal does for every tool. Shows the first exchange complete, still,
 * when `animate` is false, and picks up where it stopped when it is set back to true.
 *
 * @param props.animate - Whether the demo plays.
 * @param props.tools - The AI tools the portal covers, as one phrase, for the answers that name them.
 */
export function StreamingDemo({ animate, tools }: { animate: boolean; tools: string }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const exchanges = useMemo(() => t("signedOut.demo.exchanges", { returnObjects: true, tools }) as Exchange[], [t, tools]);
  const answers = useMemo(() => exchanges.map((exchange) => toTokens(exchange.answer)), [exchanges]);
  const [state, setState] = useState<DemoState>(() =>
    animate
      ? { exchange: 0, phase: "typing", promptChars: 0, tokens: 0, spentTokens: 0, months: 0 }
      : { exchange: 0, phase: "done", promptChars: exchanges[0].prompt.length, tokens: answers[0].length, spentTokens: 0, months: 0 },
  );

  useEffect(() => {
    if (!animate) {
      return;
    }

    const advance = (current: DemoState): DemoState => {
      switch (current.phase) {
        case "typing":
          return current.promptChars < exchanges[current.exchange].prompt.length
            ? { ...current, promptChars: current.promptChars + 1 }
            : { ...current, phase: "thinking" };
        case "thinking":
          return { ...current, phase: "streaming" };
        case "streaming":
          return current.tokens < answers[current.exchange].length ? { ...current, tokens: current.tokens + 1 } : { ...current, phase: "done" };
        case "done": {
          const next = (current.exchange + 1) % exchanges.length;
          const newMonth = next === 0;
          return {
            exchange: next,
            phase: "typing",
            promptChars: 0,
            tokens: 0,
            spentTokens: newMonth ? 0 : current.spentTokens + answers[current.exchange].length,
            months: newMonth ? current.months + 1 : current.months,
          };
        }
      }
    };

    const delays: Record<DemoPhase, () => number> = {
      typing: () => jitter(25, 70),
      thinking: () => jitter(700, 1100),
      streaming: () => jitter(30, 110),
      done: () => 3200,
    };

    const timer = window.setTimeout(() => setState(advance), delays[state.phase]());
    return () => window.clearTimeout(timer);
  }, [animate, state, exchanges, answers]);

  const exchange = exchanges[state.exchange];
  // A finished exchange is shown whole, even if its text changed after it finished (the tool names arrive after the page does).
  const done = state.phase === "done";
  const promptChars = done ? exchange.prompt.length : state.promptChars;
  const answerTokens = done ? answers[state.exchange].length : state.tokens;
  const tokens = state.spentTokens + answerTokens;
  const cost = (tokens * usdPerMillionTokens) / 1_000_000;
  const used = Math.min(cost / allowanceUsd, 1);
  const money = (amount: number, digits: number): string => formatDemoMoney(i18n.language, amount, digits);

  const status =
    used >= 1
      ? t("signedOut.demo.allowanceReached")
      : state.months > 0 && state.exchange === 0 && state.phase === "typing"
        ? t("signedOut.demo.newMonth")
        : t("signedOut.demo.allowanceLabel", { allowance: money(allowanceUsd, 4) });

  return (
    <DemoPanel
      windowTitle={t("signedOut.demo.windowTitle")}
      phase={state.phase}
      prompt={exchange.prompt.slice(0, promptChars)}
      answer={answers[state.exchange].slice(0, answerTokens)}
      tokens={new Intl.NumberFormat(i18n.language).format(tokens)}
      cost={money(cost, 5)}
      rate={t("signedOut.demo.rateValue", { rate: money(usdPerMillionTokens, 0) })}
      meter={{ label: t("signedOut.demo.allowanceMeter"), level: used, status }}
    />
  );
}
